import { StandardContainer } from '@design-system/StandardContainer';
import { NavBar } from '@design-system/NavBar';
import { HealthL3HeaderDetails } from '@screens/HealthPages/HealthL3HeaderDetails';
import { Image } from '@design-system/Image';
import { sanitizeString } from '@utils/string/sanitizeString';
import { ScoreDetail } from '@viome/viome-base/src/usecase/Health/Health.model';
import { Skeleton } from '@design-system/Skeleton';
import { Trends } from '@design-system/Trends';
import { transformTrendToVGraph } from '@/utils/health/transformVGraph';
import { getL2ImageUrl } from '@utils/nutrition/getImageUrl';
import { useGetScoreReferenceV2 } from '@viome/viome-base/src/usecase/Health/HealthQuery';

interface HealthDetailsSectionHeaderProps {
  scoreDetail: ScoreDetail;
  isScoreDetailsAPILoading: boolean;
  scoreId: number;
  isL4ScoreDetailAPIError: boolean;
  /** Rendered above "Related Health Zone" — used to reorder Disease Risk to the top for PRO users. */
  topSection?: React.ReactNode;
}

export const HealthDetailsSectionHeader = ({
  scoreDetail,
  isScoreDetailsAPILoading,
  scoreId,
  isL4ScoreDetailAPIError,
  topSection,
}: HealthDetailsSectionHeaderProps) => {
  const sanitizedScoreRank = sanitizeString(scoreDetail?.rank, {
    lowerCase: true,
  });

  const {
    about: aboutInfo,
    related_health_zones: relatedHealthZones = [],
    trend,
  } = scoreDetail || {};

  const { data: referenceData } = useGetScoreReferenceV2(scoreId);

  if (isL4ScoreDetailAPIError) {
    return;
  }

  return (
    <div>
      <section
        data-product={sanitizedScoreRank}
        className="w-full !max-w-full transition-colors bg-gradient-to-b data-[product='attention']:from-[#B18383] data-[product='attention']:to-[#000] data-[product='maintain']:from-[#87B9B0E5] data-[product='maintain']:to-[#000] data-[product='improve']:from-[#B9B687E5] data-[product='improve']:to-[#000] from-[#000] to-[#000]"
      >
        <StandardContainer>
          <NavBar
            returnButtonTitle="Back"
            className="md:-ml-5 mt-12 text-primary"
          />
          <HealthL3HeaderDetails
            l3Score={scoreDetail}
            isLoading={isScoreDetailsAPILoading}
            scoreId={scoreId}
          />
          {trend && trend.length > 1 && (
            <div className="mt-12">
              {isScoreDetailsAPILoading ? (
                <Skeleton className="h-32 w-full rounded-lg" />
              ) : (
                <div>
                  <Trends
                    trend={transformTrendToVGraph(trend) || []}
                    topIndicator="100"
                    bottomIndicator="0"
                    referenceData={referenceData}
                  />
                </div>
              )}
            </div>
          )}
        </StandardContainer>
      </section>
      {topSection}
      <section data-testid="healthDetailsHeader-relatedHealthZonesSection">
        <StandardContainer>
          {isScoreDetailsAPILoading ? (
            <>
              <Skeleton className="h-8 w-48 mb-6" data-testid="healthDetailsHeader-relatedZonesTitleSkeleton" />
              <div className="flex flex-row flex-grow flex-wrap gap-3" data-testid="healthDetailsHeader-relatedZonesSkeletonContainer">
                <Skeleton className="w-60 h-20 mt-6 justify-start" />
                <Skeleton className="w-60 h-20 mt-6 justify-start" />
              </div>
            </>
          ) : (
            <>
              {relatedHealthZones?.length > 0 && (
                <>
                  <span className="typography-h1 text-primary" data-testid="healthDetailsHeader-relatedZonesTitle">
                    Related Health Zone
                  </span>
                  <div className="flex flex-row flex-grow flex-wrap gap-6 mt-6" data-testid="healthDetailsHeader-relatedZonesList">
                    {relatedHealthZones?.map((healthZone, index) => (
                      <div className="flex items-center" key={healthZone.id} data-testid={`healthDetailsHeader-relatedZone-${index}`}>
                        <div data-testid={`healthDetailsHeader-relatedZoneIcon-${index}`}>
                          <Image
                            className="max-w-12"
                            src={getL2ImageUrl(healthZone.image_url)}
                            data-testid={`healthDetailsHeader-relatedZoneImage-${index}`}
                          />
                        </div>
                        <div className="text-primary typography-h3 ml-1 truncate" data-testid={`healthDetailsHeader-relatedZoneTitle-${index}`}>
                          {healthZone.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </StandardContainer>
      </section>
      <section>
        <div className="mt-12">
          <StandardContainer>
            {isScoreDetailsAPILoading ? (
              <>
                <Skeleton className="h-8 w-48 mb-6" />
                <Skeleton className="h-28 max-w-[35rem]" />
              </>
            ) : (
              <>
                <span className="typography-h1 text-primary">
                  About This Score
                </span>
                <span className="typography-body text-secondary mt-4 max-w-[35rem]">
                  {aboutInfo}
                </span>
              </>
            )}
          </StandardContainer>
        </div>
      </section>
    </div>
  );
};
